<template>
  <div id="email_engagement_activity" class="flex flex-col gap-2">
    <ContainerInfoItem
      :source="`email_engagement_activity_${type}`"
      :title="getTitle"
      :description="range.label"
      height="fit-content"
    >
      <SkeletonLoader
        v-if="isLoading"
        :page="
          type === analyticTypes.TABLE ? loaderPages.TABLE : loaderPages.CHART
        "
      />

      <template v-else>
        <CustomTable
          v-if="type === analyticTypes.TABLE && allTableData?.length > 0"
          :headers="emailEngagementTableHeaders"
          :data="paginatedData"
          :show-page-size="false"
          :pagination="pagination"
          @update:pagination="handlePageChange"
          class="mt-4"
        />

        <LineChart
          v-else-if="
            type === analyticTypes.CHART && chartCategories && chartData
          "
          id="email_engagement_activity"
          :categories="chartCategories"
          :series-data="chartData"
        />

        <EmptyScreen
          v-else
          :source="`email_engagement_activity_${type}`"
          text="No data found!"
          :type="emptyScreenTypes.CHART_AND_TABLE"
        />
      </template>
    </ContainerInfoItem>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { processLineChartData } from "@/src/utils/chartUtils.js";
import {
  analyticTypes,
  emailEngagementTableHeaders,
} from "@/src/constants/analyticConstants.js";
import store from "@/src/store/index.js";
import AnalyticService from "@/src/api/AnalyticService.js";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
import LineChart from "@/src/components/charts/LineChart.vue";
import { emptyScreenTypes } from "@/src/constants/searchConstants.js";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";

const props = defineProps({
  type: {
    type: String,
    required: true,
    validator: (value) =>
      [analyticTypes.TABLE, analyticTypes.CHART].includes(value),
  },
});

const range = computed(() => store.getters["analytics/getRange"]);
const userIds = computed(() => store.getters["analytics/getUserIds"]);
const teamIds = computed(() => store.getters["analytics/getTeamIds"]);

const isLoading = ref(false);
const allTableData = ref([]);
const chartCategories = ref(null);
const chartData = ref(null);
const pagination = ref({
  current: 1,
  pageSize: 5,
  total: 0,
});

const paginatedData = computed(() => {
  return allTableData.value || [];
});

const getTitle = computed(() => {
  return "Email engagement activity";
});

const resetData = () => {
  chartCategories.value = null;
  chartData.value = null;
  allTableData.value = [];
  pagination.value = {
    current: 1,
    pageSize: 5,
    total: 0,
  };
};

const handlePageChange = (newPagination) => {
  pagination.value = { ...pagination.value, ...newPagination };
  fetchData();
};

const processChartData = (data) => {
  if (!data?.length) {
    resetData();
    return;
  }

  const { categories, seriesData } = processLineChartData(data, {
    dateKey: "date",
    metrics: [
      { key: "sent", label: "Emails Sent" },
      { key: "opened", label: "Email Opens" },
      { key: "clicked", label: "Email Clicks" },
      { key: "bounced", label: "Bounces" },
    ],
  });

  chartCategories.value = categories;
  chartData.value = seriesData;

  if (
    !chartData.value.some((series) => series.data.some((value) => value !== 0))
  ) {
    resetData();
  }
};

const getParams = () => {
  const baseParams = {
    start_date: range.value?.start,
    end_date: range.value?.end,
    channel_type: "EMAIL"
  };

  if (props.type === analyticTypes.TABLE) {
    return {
      ...baseParams,
      page: pagination.value.current,
      page_size: pagination.value.pageSize,
    };
  }

  return baseParams;
};

const fetchData = async () => {
  if (!range.value?.start || !range.value?.end) {
    return;
  }

  if (isLoading.value) {
    return;
  }

  try {
    isLoading.value = true;
    const params = getParams();

    let response;
    if (props.type === analyticTypes.TABLE) {
      response =
        await AnalyticService.V2.fetchMarketingCampaignSentByDateRange(params);
    } else {
      response = await AnalyticService.V2.fetchWorkspaceMarketingCampaignLine(params);
    }

    if (response) {
      if (props.type === analyticTypes.TABLE) {
        let campaigns = null;
        if (response.success && response.campaigns) {
          campaigns = response.campaigns;
        } else if (response.campaigns) {
          campaigns = response.campaigns;
        } else if (response.success && response.data) {
          campaigns =
            response.data.campaigns || response.data.data || response.data;
        } else if (Array.isArray(response)) {
          campaigns = response;
        }

        if (Array.isArray(campaigns)) {
          allTableData.value = campaigns.map((campaign) => ({
            campaign_name: campaign.name || "",
            emails_sent: campaign.total_sent || 0,
            emails_opened: campaign.total_opened || 0,
            emails_clicked: campaign.total_clicked || 0,
            emails_delivered: campaign.total_delivered || 0,
            emails_bounced: campaign.total_bounced || 0,
          }));
        } else {
          allTableData.value = [];
        }

        pagination.value.total = response.total || allTableData.value.length;
      } else {
        let chartData = null;
        if (response.success && response.data) {
          chartData =
            response.data.daily_metrics || response.data.data || response.data;
        } else if (response.daily_metrics) {
          chartData = response.daily_metrics;
        } else if (Array.isArray(response)) {
          chartData = response;
        }
        processChartData(Array.isArray(chartData) ? chartData : []);
      }
    } else {
      resetData();
    }
  } catch (error) {
    console.error("Error fetching email engagement:", error);
    resetData();
  } finally {
    isLoading.value = false;
  }
};

watch(
  [range],
  () => {
    if (range.value?.start && range.value?.end) {
      if (props.type === analyticTypes.TABLE) {
        pagination.value.current = 1;
      }
      resetData();
      fetchData();
    }
  },
  { deep: true, immediate: true }
);

  useAnalyticsRefresh(fetchData, [range, userIds, teamIds]);
</script>